/**
 * Human-readable dates. Turns a local `YYYY-MM-DD` into "Today" / "Tomorrow" /
 * "Wed, 21 May" so users never see a raw ISO string.
 */
import { jsLocale, type SupportedLocale } from './i18n';

type T = (key: 'common.today' | 'common.tomorrow') => string;

/** True from ~2h before departure to 30min after — the trip-confirmation window. */
export function inConfirmWindow(iso: string): boolean {
  const dep = new Date(iso).getTime();
  if (!Number.isFinite(dep)) return false;
  const now = Date.now();
  return now >= dep - 2 * 60 * 60 * 1000 && now <= dep + 30 * 60 * 1000;
}

/**
 * Belgrade-local `YYYY-MM-DD` + `HH:MM` → the true UTC Date. CET/CEST aware
 * (DST: last Sun of March → last Sun of October). Mirrors the converter used
 * by the publish flow so edited departures use identical timezone semantics.
 */
export function belgradeLocalToUtc(date: string, time: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const tentativeUtc = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
  const year = y ?? 1970;
  const dstStart = (() => {
    const x = new Date(Date.UTC(year, 3, 0));
    return Date.UTC(year, 2, x.getUTCDate() - x.getUTCDay(), 1);
  })();
  const dstEnd = (() => {
    const x = new Date(Date.UTC(year, 10, 0));
    return Date.UTC(year, 9, x.getUTCDate() - x.getUTCDay(), 1);
  })();
  const offsetHours = tentativeUtc >= dstStart && tentativeUtc < dstEnd ? 2 : 1;
  return new Date(tentativeUtc - offsetHours * 60 * 60 * 1000);
}

/** Inverse of {@link belgradeLocalToUtc}: a UTC ISO instant → the date + time
 * inputs (Belgrade wall-clock) used to prefill the edit form. */
export function utcToBelgradeLocal(iso: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Belgrade',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  // Intl can emit '24' for midnight in some engines; normalize to '00'.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${hour}:${get('minute')}` };
}

export function humanDate(ymd: string, locale: SupportedLocale, t: T): string {
  const parts = ymd.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return ymd;
  const date = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 0) return t('common.today');
  if (diffDays === 1) return t('common.tomorrow');
  return date.toLocaleDateString(jsLocale(locale), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
